import {Paper} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useQueryClient} from "react-query";
import {useGetOrganizations, useGetOrganizationSettings} from "../services/ContentManager";
import {
    createAdmin,
    deleteUser,
    editUser,
    encodeTable,
    exportUsers,
    getUserTargets,
    resolveUsers,
    useGetUsersPaged
} from "../services/UserDataManager";
import UserTable from "../components/tables/UserTable";
import RenderUserEnrollments, {formatUserEnrollments} from "../components/cellRender/RenderUserEnrollments";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {DASH_USERS, getErrorMessage, post, USERS} from "../services/Client";
import AddIcon from "@mui/icons-material/Add";
import StartIconButton from "../components/buttons/StartIconButton";
import AddUserModal from "../components/modals/AddUserModal";
import {UserContext} from "./App";
import {roles} from "../services/ability";
import AlertModal from "../components/modals/AlertModal";

const DEFAULT_SORT = {field: "lastLoggedIn", sort: "desc"};

export function UserManagement() {

    const {t} = useTranslation();
    const queryClient = useQueryClient();
    let {organizations = []} = useGetOrganizations()
    let {settings} = useGetOrganizationSettings(organizations[0] && organizations[0].id)
    const {enqueueSnackbar} = useSnackbar();
    const user = useContext(UserContext);

    // Filtri, ordinamento e pagina vanno al backend: la tabella non scarica piu' l'intera anagrafica.
    const [table, setTable] = useState({filters: [], pagination: {page: 0, pageSize: 25}, sort: DEFAULT_SORT});
    const {page, status, isFetching} = useGetUsersPaged(encodeTable(table));
    // Durante il caricamento la tabella resta vuota; il totale invece viene dalla risposta precedente
    // (keepPreviousData), altrimenti con rowCount a 0 la griglia riporterebbe la pagina a zero.
    const users = isFetching ? [] : page?.content || [];
    // Messaggi ed esportazione lavorano su tutte le righe filtrate, non sulla pagina visibile.
    const filtersOnly = () => encodeTable({filters: table.filters, sort: table.sort});
    const refresh = () => queryClient.invalidateQueries([DASH_USERS]);

    let [showSerialNumber, setShowSerialNumber] = useState(false)
    let [showIban, setShowIban] = useState(false)
    let [isAddingAdmin, setIsAddingAdmin] = useState(false);
    const [alert, setAlert] = useState({
        callBack: () => {
        }, message: null, open: false
    });

    const customColumn = [
        {
            field: 'enrollments',
            headerName: t('initiatives'),
            width: 250,
            filterable: false,
            sortable: false,
            //hide: true,
            valueFormatter: ({value}) => value ? formatUserEnrollments(value, t) : "",
            renderCell: ({value, colDef}) => value && <RenderUserEnrollments enrollments={value} width={colDef.width}/>
        }
    ]

    useEffect(() => {
        if (organizations.length !== 0) {
            if (organizations.length === 1) {
                if (settings.length !== 0 && settings.find(s => s.organizationSetting === "serialNumberRequirement").value === "true") setShowSerialNumber(true)
                if (settings.length !== 0 && settings.find(s => s.organizationSetting === "ibanRequirement").value === "true") setShowIban(true)
            } else {
                setShowSerialNumber(true)
                setShowIban(true)
            }
        }
    }, [settings])

    const downloadCsv = () => {
        exportUsers(filtersOnly())
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], {type: "text/csv"}));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${t('users')}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}));
    }

    const serverSide = {
        // La Page arriva come {content, page: {totalElements}}; la forma piatta resta come ripiego.
        rowCount: page?.page?.totalElements ?? page?.totalElements ?? 0,
        // Pagina controllata: un cambio di filtri riporta la griglia alla prima pagina insieme ai dati.
        page: table.pagination.page,
        onFilterModelChange: (model) => setTable(prev => ({
            ...prev,
            pagination: {...prev.pagination, page: 0},
            filters: model.items.map(f => ({
                filter: f.columnField,
                operation: f.operatorValue,
                value: f.value === undefined || f.value === null || f.value === "" ? []
                    : (Array.isArray(f.value) ? f.value : [f.value]).map(String)
            }))
        })),
        onSortModelChange: (model) => setTable(prev => ({...prev, sort: model.length > 0 ? model[0] : DEFAULT_SORT})),
        onPageChange: (newPage) => setTable(prev => ({...prev, pagination: {...prev.pagination, page: newPage}})),
        onPageSizeChange: (size) => setTable(prev => ({...prev, pagination: {page: 0, pageSize: size}})),
        loadAllTargets: () => getUserTargets(filtersOnly()),
        resolveUsers: (values) => resolveUsers(values),
        exportCsv: downloadCsv
    };

    const saveEdit = (uid, field, value) => {
        enqueueSnackbar(t('saving...'), {variant: "info"});
        return editUser(uid, field, value)
            .then(() => enqueueSnackbar(t('saved'), {variant: "success"}))
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
            .finally(refresh)
    }

    const createNewAdmin = (values) => {
        let newAdmin = {...values}
        if (newAdmin.birthDate) newAdmin.birthDate = new Date(newAdmin.birthDate).getTime();
        enqueueSnackbar(t('saving...'), {variant: "info"});
        createAdmin(newAdmin)
            .then(() => {
                refresh();
                enqueueSnackbar(t('saved'), {variant: "success"});
            })
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
    }

    const deleteUsr = (uid) => {
        enqueueSnackbar(t('saving...'), {variant: "info"});
        deleteUser(uid)
            .then(() => {
                refresh();
                enqueueSnackbar(t('deleted'), {variant: "success"});
            })
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
    }

    const openDeleteAlert = (user) => {
        setAlert({
            open: true,
            callBack: () => deleteUsr(user.uid),
            message: <>{t('deleteUserAlertMsg')} <br/>{t('email')}: {user.email} <br/>UID: {user.uid}</>
        });
    }

    const sendEmailValidation = (uid) => {
        enqueueSnackbar(t('sending...'), {variant: "info"});
        post(`${USERS}/${uid}/sendEmailVerification`)
            .then(() => enqueueSnackbar(t('sent'), {variant: "success"}))
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
    }

    const openSendEmailAlert = (uid) => {
        setAlert({open: true, callBack: () => sendEmailValidation(uid), message: `${t('sendEmailAlertMsg')}`});
    }

    return <Paper style={{padding: "2rem"}}>
        <UserTable users={users} saveEdit={saveEdit}
                   serverSide={serverSide}
                   allowEditEmail
                   sendEmailValidation={openSendEmailAlert}
                   deleteUser={openDeleteAlert}
                   showIban={showIban}
                   showSerialNumber={showSerialNumber}
                   customColumns={customColumn}
                   // keepPreviousData lascia lo stato a "success" durante i ricaricamenti: senza
                   // isFetching l'overlay comparirebbe solo al primo caricamento
                   status={(status === "loading" || isFetching) && "loading"}
                   rightButton={
                       user.userType === roles.ADMIN &&
                       <StartIconButton onClick={() => setIsAddingAdmin(true)} title={t('addAdmin')}
                                        startIcon={<AddIcon/>}/>
                   }
                   selectUsersByEmailVisible
        />
        <AddUserModal open={!!isAddingAdmin} onClose={() => setIsAddingAdmin(false)} onSave={createNewAdmin}/>
        <AlertModal open={alert.open} onClose={() => setAlert({open: false})} onSave={alert.callBack}
                    message={alert.message}/>
    </Paper>

}
